// Pinia store for the app-wide confirm-dialog primitive.
//
// Usage in any view:
//
//     import { useConfirmStore } from '@/stores/confirm';
//     const confirm = useConfirmStore();
//     ...
//     if (!await confirm.ask({
//       title: 'Delete schedule?',
//       body:  '"Monday digest" will be archived. Past runs stay in history.',
//       danger: true,
//     })) return;
//
// One <ConfirmModal /> mounted in AppShell renders whichever request is
// active. Resolves the promise with the user's choice - no callbacks,
// no prop-drilling, no native window.confirm() that breaks the design
// system across browsers / OS.

import { defineStore } from 'pinia';
import { ref } from 'vue';

export interface ConfirmRequest {
  title:        string;
  body?:        string;
  /// `true` styles the confirm button as destructive (red) and bumps
  /// the dialog's focus to the cancel button so an accidental Enter
  /// dismisses rather than confirms. Use for delete / forget /
  /// uninstall / revoke flows.
  danger?:      boolean;
  /// Defaults to "Confirm" (or "Delete" when danger).
  confirmLabel?: string;
  /// Defaults to "Cancel".
  cancelLabel?: string;
  /// When set, the dialog renders a text input and keeps the confirm
  /// button disabled until the user types this exact string (trimmed).
  /// Use for irreversible, high-stakes destroys - typing the resource's
  /// name/id forces a deliberate pause. Pairs naturally with `danger`.
  confirmText?: string;
  /// Optional opt-in checkbox shown above the footer. The chosen state
  /// is passed to `onConfirm` as `ctx.checked`. Use for a per-confirm
  /// modifier (e.g. "also delete delivered files"). Omit for no checkbox.
  checkbox?: {
    label:    string;
    /// Initial state. Defaults to `false` (unchecked).
    default?: boolean;
  };
  /// Optional async action run when the user confirms. When set, the
  /// dialog stays open with a busy spinner until it resolves, then
  /// closes and `ask()` resolves `true`. If it throws, the dialog stays
  /// open and shows the error so the user can retry or cancel - `ask()`
  /// resolves `false` only on cancel. Use for slow destroys (VM/disk
  /// teardown) so the user gets feedback instead of a frozen-looking gap.
  /// `ctx.checked` carries the `checkbox` state (false when no checkbox).
  /// Without it, confirm resolves `true` immediately (caller runs the work).
  onConfirm?: (ctx: { checked: boolean }) => Promise<void>;
}

interface PendingRequest extends ConfirmRequest {
  resolve: (ok: boolean) => void;
}

export const useConfirmStore = defineStore('confirm', () => {
  const pending = ref<PendingRequest | null>(null);

  function ask(req: ConfirmRequest): Promise<boolean> {
    // If another request is already open, queue would be needlessly
    // complicated; just reject the existing one as "cancel" and start
    // the new one. Two simultaneous deletes is a UX corner case we
    // don't have to design for today.
    if (pending.value) {
      pending.value.resolve(false);
    }
    return new Promise<boolean>((resolve) => {
      pending.value = { ...req, resolve };
    });
  }

  function resolveAndClose(ok: boolean): void {
    const cur = pending.value;
    pending.value = null;
    if (cur) cur.resolve(ok);
  }

  return { pending, ask, resolveAndClose };
});
